import { Controller } from '@hotwired/stimulus'

/**
 * Countdown timer controller for OTP resend button.
 *
 * Disables the button for a specified number of seconds, showing a countdown.
 * When countdown reaches zero, enables the button.
 * Clicking the button sends a POST request to resend the OTP and restarts the countdown.
 *
 * Usage:
 *   <button data-controller="kycs--resend-timer"
 *           data-kycs--resend-timer-seconds-value="60"
 *           data-kycs--resend-timer-url-value="/kycs/sg/resend-otp/"
 *           data-action="click->kycs--resend-timer#resend"
 *           disabled>
 *     Resend code (<span data-kycs--resend-timer-target="countdown">60</span>s)
 *   </button>
 */
export default class extends Controller {
  static targets = ['countdown']
  static values = {
    seconds: { type: Number, default: 60 },
    url: String
  }

  connect() {
    this.remaining = this.secondsValue
    this.readyText = this.element.dataset.readyText || 'Resend code'
    this.startCountdown()
  }

  disconnect() {
    this.stopCountdown()
  }

  startCountdown() {
    this.element.disabled = true
    this.updateDisplay()

    this.timer = setInterval(() => {
      this.remaining--
      this.updateDisplay()

      if (this.remaining <= 0) {
        this.stopCountdown()
        this.enableButton()
      }
    }, 1000)
  }

  stopCountdown() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  updateDisplay() {
    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = this.remaining
      this.element.innerHTML = `${this.readyText} (<span data-kycs--resend-timer-target="countdown">${this.remaining}</span>s)`
    }
  }

  enableButton() {
    this.element.disabled = false
    this.element.textContent = this.readyText
  }

  async resend(event) {
    event.preventDefault()

    if (this.element.disabled) return

    // Disable and show sending state
    this.element.disabled = true
    this.element.textContent = 'Sending...'

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRFToken': this.getCSRFToken(),
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Reset countdown and restart
        this.remaining = this.secondsValue
        this.startCountdown()
      } else {
        // Re-enable button on error
        this.enableButton()
      }
    } catch (error) {
      console.error('Failed to resend OTP:', error)
      this.enableButton()
    }
  }

  getCSRFToken() {
    return document.querySelector('[name=csrfmiddlewaretoken]')?.value ||
           document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1]
  }
}
